import React, { Fragment, useState, useEffect, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Form, Modal, Button, Nav, Tab } from "react-bootstrap";
import * as Api from "../../General/api";
import * as Common from "../../General/common";
import * as Yup from "yup";
import { useFormik } from "formik";
import NpiTab from "../../components/npiTab";
import NameTab from "../../components/nameTab";
import Loader from "react-loader-spinner";

const AddEditPhysician = (props) => {
  const [doctors, setDoctors] = useState([]);
  const [isChangeDetect, setChangeDetect] = useState(false);
  
  // Determine initial tab and values from props
  const getInitialTab = () => {
    if (props.npi) return "npi";
    if (props.firstName || props.lastName) return "name";
    return "npi"; // default
  };
  
  const [activeKey, setActiveKey] = useState(() => {
    if (props.npi) return "npi";
    if (props.firstName || props.lastName) return "name";
    return "npi"; // default
  });
  const [selectedNpi, setSelectedNpi] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset when modal opens with new props
  useEffect(() => {
    if (props.open) {
      const initialTab = getInitialTab();
      setActiveKey(initialTab);
      setDoctors([]);
      setSelectedNpi(null);
      setSelectedDoctor(null);
      setChangeDetect(false);
    }
  }, [props.open, props.npi, props.firstName, props.lastName]);


  const Schema = Yup.object().shape({
    firstName: Yup.string()
      .max(50, "Maximum limit 50 characters")
      .typeError("Invalid Value")
      .matches(/^[A-Za-z ']*$/, "Please enter valid first name")
      .required("First Name is required"),
    lastName: Yup.string()
      .max(50, "Maximum limit 50 characters")
      .typeError("Invalid Value")
      .matches(/^[A-Za-z ']*$/, "Please enter valid last name")
      .required("Last Name is required"),
    npi: Yup.string()
      .min(10, "Minimum limit 10 numbers")
      .max(10, "Maximum limit 10 numbers")
      .typeError("Invalid Value")
      .required("NPI is required"),
  });

  const formik = useFormik({
    initialValues: { physicianId: 0, firstName: "", lastName: "", npi: "" },
    validationSchema: Schema,
    onSubmit: (values, { setSubmitting }) => {
      handleValidSubmit(values);
      setSubmitting(false);
    },
  });

  const getSpecialty = (row) =>
    row?.speciality ?? row?.specialty ?? row?.credential ?? "";

  const getIsPecosCertified = (row) => {
    const v =
      row?.isPecosCertified ??
      row?.isPECOSCertified ??
      row?.pecosCertified ??
      row?.pecos ??
      null;
    return v === true || v === "true" || v === 1;
  };

  const onRowCheck = (row, checked) => {
    if (!checked) {
      setSelectedNpi(null);
      setSelectedDoctor(null);
      return;
    }
    setSelectedNpi(row.npi);
    setSelectedDoctor(row);
    setChangeDetect(true);
  };

  // Memoize the callback to prevent infinite loops
  const handleDoctorsFetched = useCallback((list) => {
    setDoctors(list || []);
    setSelectedNpi(null);
    setSelectedDoctor(null);
  }, []);

  const handleValidSubmit = async (values) => {
    setLoading(true);
    values.npi = values.npi.toString();
    Api.postRequest(Api.URI_UpsertPhysician, values)
      .then((res) => {
        if (res.data.success) {
          Common.showSuccessAlert(
            "Physician created successfully",
            "success",
            "Success!",
            3000
          );
          props.onSaveAPI({
            physicianId: res.data.result,
            npi: values.npi,
            lastName: values.lastName,
            firstName: values.firstName,
          });
          props.toggleModal();
        } else {
          Common.showSuccessAlertAutoClose(
            res.data.message,
            "warning",
            "Warning!",
            3000
          );
        }
        setLoading(false);
      })
      .catch((err) => console.log(err));
  };

  const handleCloseModal = () => {
    Common.showConfirmation(
      "Are you sure you want to close?",
      "Warning",
      () => props.toggleModal(),
      () => {},
      null,
      "Close"
    );
  };

  const doctorColumns = [
    { name: "First Name", selector: (row) => row.firstName, sortable: true, wrap: true, grow: 1, maxWidth: "120px" },
    { name: "Last Name",  selector: (row) => row.lastName,  sortable: true, wrap: true, grow: 1, maxWidth: "120px" },
    { name: "NPI",        selector: (row) => row.npi,       sortable: true, wrap: true, width: "120px" },
    // { name: "Specialty" , selector: (row) => row.taxonomyDescription, sortable: true, wrap: true, grow: 1, maxWidth: "120px"},
    {
  name: "Specialty",
  selector: row => row.taxonomyDescription,  // keeps sort/filter working
  sortable: true,
  grow: 0,                // don't expand this column
  width: "120px",         // fix the column width (stronger than maxWidth)
  // wrap: false,          // (default) make sure it's single line
  cell: row => (
    <span
      title={row.taxonomyDescription || ""}     // native tooltip with full text
      style={{
        display: "inline-block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {row.taxonomyDescription}
    </span>
  )
},
    { name: "State",      selector: (row) => row.state,     sortable: true, width: "60px", center: true },
    {
      name: "",
      cell: (row) => (
        <input
          type="checkbox"
          onChange={(e) => onRowCheck(row, e.target.checked)}
          checked={selectedNpi === row.npi}
          style={{ width: 18, height: 18, cursor: "pointer" }}
          aria-label={`select-${row.npi}`}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "40px",
      center: true,
    },
  ];

  const onTabSelect = (k) => {
    setActiveKey(k);
    setDoctors([]);
    setSelectedNpi(null);
    setSelectedDoctor(null);
    setChangeDetect(false);
  };

//  const showPecosWarning = selectedDoctor && !getIsPecosCertified(selectedDoctor);

  return (
    <Fragment>
      <Modal
        size="lg"
        centered
        scrollable
        backdrop="static"
        keyboard={false}
        show={props.open}
        onHide={handleCloseModal}
        dialogClassName="physician-modal-dialog"
        contentClassName="physician-modal"
      >
        <Modal.Header className="py-2">
                  <span className="align-middle">{props.modalTitle || "Add New Physician"}</span>
        </Modal.Header>

        <Modal.Body className="py-2">
          <Form>
            <Tab.Container activeKey={activeKey} onSelect={onTabSelect}>
              <Nav
                variant="pills"
                className="d-flex justify-content-center physician-tabs"
              >
                <Nav.Item>
                  <Nav.Link varient="success" eventKey="npi">By NPI</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link varient="success" eventKey="name">By Name</Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="npi">
                  <div className="physician-search">
                    <NpiTab
                      onDoctorsFetched={handleDoctorsFetched}
                      resetSignal={activeKey}
                      setIsLoading={setLoading}
                      initialNpi={props.npi}
                    />
                  </div>
                </Tab.Pane>

                <Tab.Pane eventKey="name">
                  <div className="physician-search">
                    <NameTab
                      onDoctorsFetched={handleDoctorsFetched}
                      resetSignal={activeKey}
                      setIsLoading={setLoading}
                      initialFirstName={props.firstName}
                      initialLastName={props.lastName}
                    />
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
<div className="physician-results react-dataTable">
              {isLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <Loader type="Puff" color="#00BFFF" height={50} width={50} />
                </div>
              ) : (
                <DataTable
                  columns={doctorColumns}
                  data={doctors}
                  highlightOnHover
                  striped
                  responsive
                  dense
                  fixedHeader
                  fixedHeaderScrollHeight="160px"
                  pagination={false}
                  noDataComponent=""
                />
              )}
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer className="py-2 physician-footer" style={{ justifyContent: "flex-end" }}>
          <Button variant="danger" className="btn-sm" onClick={handleCloseModal}>
            <span className="align-middle">Close</span>
          </Button>
          <Button variant = "primary"
  type="button"
  className="btn-sm"
  disabled={!selectedDoctor || isSubmitting}
  onClick={async () => {
    if (!selectedDoctor) return;
    setIsSubmitting(true);

    try {
      const res = await Api.postRequest(Api.URI_UpsertPhysicianNew, {
        ...selectedDoctor,
        createdBy: props.userID,
        modifiedBy: props.userID,
        facilityId: props.facilityId,
        active: true,
        isEnriched: false,
      });

      if (res.data.success) {
        props.onSaveAPI(res.data.result);
        props.toggleModal();
      }
      // no popup anymore
    } catch (ex) {
      Common.LogError(ex, "Physician", "UpsertPhysicianFromGrid");
      // optional: handle silent error or inline message
    } finally {
      setIsSubmitting(false);
    }
  }}
  style={{ minWidth: 90, marginLeft: 8 }}
  //variant="success"
>
  {isSubmitting ? (
    <>
      <span
        className="spinner-border spinner-border-sm me-5"
        role="status"
        aria-hidden="true"
        style={{ marginRight: "10px" }}
      ></span>
         Loading...
    </>
  ) : (
    <span className="align-middle">
      {props.modalTitle === "Sync Physician" ? "Sync Physician" : "Add"}
    </span>
  )}
</Button>

        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default AddEditPhysician;
